"use client";

import { cn } from "@/lib/utils";
import { animate, motion, useMotionTemplate, useMotionValue, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useRef, useState, ComponentType } from "react";
const CHARACTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
function generateRandomString(length: number) {
  let result = "";
  for (let i = 0; i < length; i += 1) {
    result += CHARACTERS.charAt(Math.floor(Math.random() * CHARACTERS.length));
  }
  return result;
}
interface PrivateKeySecurityHeroProps {
  icon: ComponentType<{
    className?: string;
  }>;
  eyebrow: string;
  title: string;
  description: string;
  accentClassName?: string;
  layout?: "horizontal" | "vertical";
  maskRadius?: number;
  logoLightUrl?: string;
  logoDarkUrl?: string;
  className?: string;
}
export function PrivateKeySecurityHero({
  icon: Icon,
  eyebrow,
  title,
  description,
  accentClassName = "from-green-400 via-sky-500 to-blue-700",
  layout = "horizontal",
  maskRadius = 220,
  logoLightUrl,
  logoDarkUrl,
  className
}: PrivateKeySecurityHeroProps) {
  const shouldReduceMotion = useReducedMotion();
  const mouseX = useMotionValue(160);
  const mouseY = useMotionValue(80);
  const maskImage = useMotionTemplate`radial-gradient(${maskRadius}px at ${mouseX}px ${mouseY}px, white, transparent)`;
  const [randomString, setRandomString] = useState("");
  const frameIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    setRandomString(generateRandomString(1200));
  }, []);
  const staticNoise = useMemo(() => generateRandomString(1200), []);
  useEffect(() => {
    if (shouldReduceMotion) return;
    const controlsX = animate(mouseX, layout === "vertical" ? [48, 176, 260, 120, 48] : [36, 280, 220, 72, 36], {
      duration: 10,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "mirror"
    });
    const controlsY = animate(mouseY, layout === "vertical" ? [42, 120, 260, 188, 42] : [28, 48, 148, 120, 28], {
      duration: 7.5,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "mirror"
    });
    frameIntervalRef.current = window.setInterval(() => {
      setRandomString(generateRandomString(1200));
    }, 1400);
    return () => {
      controlsX.stop();
      controlsY.stop();
      if (frameIntervalRef.current !== null) {
        window.clearInterval(frameIntervalRef.current);
      }
    };
  }, [layout, mouseX, mouseY, shouldReduceMotion]);
  return <div className={cn("shadow-ds-lift border-ds-neutral-200/80 dark:border-ds-neutral-800/80 group relative overflow-hidden rounded-2xl border bg-white/90 p-8 dark:bg-zinc-950/80", className)}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.08),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.08),transparent_32%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.14),transparent_32%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.5),transparent_45%)] dark:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.04),transparent_45%)]" />

      {!shouldReduceMotion && <>
          <motion.div className={cn("pointer-events-none absolute inset-0 opacity-80", "bg-gradient-to-r", accentClassName)} style={{
        maskImage,
        WebkitMaskImage: maskImage
      }} />
          <motion.div className="pointer-events-none absolute inset-0 opacity-70 mix-blend-soft-light" style={{
        maskImage,
        WebkitMaskImage: maskImage
      }}>
            <p className="text-3xs absolute inset-0 overflow-hidden break-words px-5 py-4 font-mono font-bold leading-4 text-white/70">
              {randomString}
            </p>
          </motion.div>
        </>}

      {shouldReduceMotion && <div className="pointer-events-none absolute inset-0 opacity-[0.08] dark:opacity-[0.14]">
          <p className="text-ds-text-primary text-3xs absolute inset-0 overflow-hidden break-words px-5 py-4 font-mono font-bold leading-4">
            {staticNoise}
          </p>
        </div>}

      <div className={cn("relative z-10", layout === "vertical" ? "flex h-full flex-col justify-between gap-8" : "flex items-start gap-4")}>
        <div className="min-w-0">
          <p className="label-2xs font-semibold tracking-[0.22em]">{eyebrow}</p>
          <h3 className={cn("mt-1 font-semibold text-zinc-950 dark:text-white", layout === "vertical" ? "text-3xl leading-tight" : "text-lg")}>
            {title}
          </h3>
          <p className={cn("text-ds-text-subtle dark:text-ds-text-disabled mt-2", layout === "vertical" ? "max-w-[18rem] text-base leading-7" : "max-w-md text-sm leading-6")}>
            {description}
          </p>
        </div>
        {layout === "vertical" && <div className="dark:border-ds-neutral-700/70 dark:bg-ds-neutral-900/50 pointer-events-none rounded-2xl border border-white/60 bg-white/55 p-4 backdrop-blur-sm">
            <p className="label label-xs text-ds-focus-brand">Zero-Knowledge Flow</p>
            <p className="text-ds-text-secondary mt-2 text-sm leading-6">
              Client-side encryption, secure Turnkey iframe input, and HSM-backed storage after import.
            </p>

            {logoLightUrl || logoDarkUrl ? <div className="mt-4 flex items-center gap-2">
                <span className="label label-xs">Secured by</span>
                {logoLightUrl ? <img src={logoLightUrl} alt="" aria-hidden="true" className="block h-4 object-contain dark:hidden" /> : null}
                {logoDarkUrl ? <img src={logoDarkUrl} alt="" aria-hidden="true" className="hidden h-4 object-contain dark:block" /> : null}
              </div> : <Icon className="text-ds-text-primary h-5 w-5" />}
          </div>}
      </div>
    </div>;
}